import { useState, useEffect } from "react";
import {
  getDocumentsFromClient,
  updateDocumentsFromClient,
  getLeadDetails,
  type UpdateDocumentsFromClientPayload,
} from "../../api/discussion";
import { toast } from "sonner";
import { Checkbox } from "components/ui/checkbox";

type ChecklistItem = {
  id: string;
  label: string;
  checked: boolean;
  key: "documentsOfApplicantReceived" | "documentsOfSignatoryReceived" | "documentsOfMarkReceived";
};

type DocumentsFromClientViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

export default function DocumentsFromClientView({
  leadId,
  onSuccess,
}: DocumentsFromClientViewProps) {
  const [loading, setLoading] = useState(!!leadId);
  const [isDocumentsOfMarkRequired, setIsDocumentsOfMarkRequired] = useState(true);
  const [documentsOfApplicantReceived, setDocumentsOfApplicantReceived] = useState(false);
  const [documentsOfSignatoryReceived, setDocumentsOfSignatoryReceived] = useState(false);
  const [documentsOfMarkReceived, setDocumentsOfMarkReceived] = useState<boolean | null>(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setDocumentsOfApplicantReceived(false);
      setDocumentsOfSignatoryReceived(false);
      setDocumentsOfMarkReceived(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([getDocumentsFromClient(leadId), getLeadDetails(leadId)])
      .then(([docsRes, leadRes]) => {
        if (cancelled) return;
        setIsDocumentsOfMarkRequired(docsRes.isDocumentsOfMarkRequired);
        setDocumentsOfApplicantReceived(docsRes.documentsOfApplicantReceived);
        setDocumentsOfSignatoryReceived(docsRes.documentsOfSignatoryReceived);
        setDocumentsOfMarkReceived(docsRes.documentsOfMarkReceived);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : "Failed to load documents from client");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const checklistItems: ChecklistItem[] = [
    {
      id: "applicant",
      label: "Documents of Applicant",
      checked: documentsOfApplicantReceived,
      key: "documentsOfApplicantReceived",
    },
    {
      id: "signatory",
      label: "Documents of Signatory",
      checked: documentsOfSignatoryReceived,
      key: "documentsOfSignatoryReceived",
    },
    ...(isDocumentsOfMarkRequired
      ? [
          {
            id: "mark",
            label: "Documents of Mark",
            checked: documentsOfMarkReceived === true,
            key: "documentsOfMarkReceived" as const,
          },
        ]
      : []),
  ];

  const toggleCheck = (key: ChecklistItem["key"]) => {
    if (key === "documentsOfApplicantReceived") {
      setDocumentsOfApplicantReceived((prev) => !prev);
    } else if (key === "documentsOfSignatoryReceived") {
      setDocumentsOfSignatoryReceived((prev) => !prev);
    } else {
      setDocumentsOfMarkReceived((prev) => (prev === true ? false : true));
    }
  };

  const selectAll = () => {
    const allChecked = checklistItems.every((i) => i.checked);
    if (allChecked) {
      setDocumentsOfApplicantReceived(false);
      setDocumentsOfSignatoryReceived(false);
      if (isDocumentsOfMarkRequired) setDocumentsOfMarkReceived(false);
    } else {
      setDocumentsOfApplicantReceived(true);
      setDocumentsOfSignatoryReceived(true);
      if (isDocumentsOfMarkRequired) setDocumentsOfMarkReceived(true);
    }
  };

  const validateDocumentsSelection = (): boolean => {
    const isAnySelected = checklistItems.some((item) => item.checked);
    if (!isAnySelected) {
      toast.error("Please select atleast any one");
      return false;
    }
    return true;
  };

  const buildDocumentsFromClientPayload =
    (): UpdateDocumentsFromClientPayload => {
      const payload: UpdateDocumentsFromClientPayload = {
        documentsOfApplicantReceived,
        documentsOfSignatoryReceived,
      };
      if (isDocumentsOfMarkRequired) {
        payload.documentsOfMarkReceived = documentsOfMarkReceived ?? false;
      }
      return payload;
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;

    if (!validateDocumentsSelection()) return;

    setSaving(true);
    try {
      const payload = buildDocumentsFromClientPayload();
      await updateDocumentsFromClient(leadId, payload);
      toast.success("Documents from client saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading...</p>
      </div>
    );
  }

  return (
    <form
      id="documents-from-client-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Document Receipt Checklist */}
      <div className="flex flex-col gap-5 w-full max-w-[1092px]">
        <div className="flex flex-row justify-between items-start w-full gap-5">
          <h3
            className="text-base font-normal leading-5 text-[#222222] flex items-center"
            style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
          >
            Document Receipt Checklist
          </h3>
          <button
            type="button"
            onClick={selectAll}
            className="text-sm font-normal leading-5 text-[#0070F0] flex items-center hover:underline"
            style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
          >
            Select All
          </button>
        </div>

        <div className="flex flex-col gap-2 w-full">
          {checklistItems.map((item) => (
            <div
              key={item.id}
              className="box-border flex flex-col p-2.5 gap-2.5 rounded-[6px] border border-[#EEEEEE] bg-white min-h-[44px]"
            >
              <div className="flex flex-row items-center gap-2 w-full">
                <Checkbox
                  id={`documents-checklist-${item.id}`}
                  checked={item.checked}
                  onCheckedChange={() => toggleCheck(item.key)}
                  className="size-5 rounded-[6px] shrink-0"
                />
                <label
                  htmlFor={`documents-checklist-${item.id}`}
                  className="text-xs font-normal leading-5 text-[#222222] flex-1 min-w-0 cursor-pointer"
                  style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
                >
                  {item.label}
                </label>
                <span
                  className="flex items-center justify-center px-1.5 py-0.5 h-[18px] rounded-[6px] bg-[#F7F7F7] text-[10px] font-normal leading-5 text-[#222222] flex-shrink-0"
                  style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
                >
                  {item.checked ? "Received" : "Pending"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </form>
  );
}
